import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebase } from '../../../context/FirebaseContext';
import './AdminLoginPage.css';

const AdminLoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [resetSuccess, setResetSuccess] = useState('');
    const [resetError, setResetError] = useState('');

    const {
        currentUser,
        storeInfo,
        isAuthenticated,
        login,
        resetPassword
    } = useFirebase();

    const navigate = useNavigate();

    // Redirect if already authenticated
    useEffect(() => {
        if (currentUser && storeInfo && isAuthenticated()) {
            navigate('/admin/dashboard', { replace: true });
        }
    }, [currentUser, storeInfo, isAuthenticated, navigate]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;

        setLoading(true);
        setError('');

        try {
            // Basic validation
            if (!email.trim() || !password.trim()) {
                throw new Error('Please enter both email and password');
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(email.trim())) {
                throw new Error('Please enter a valid email address');
            }

            // Attempt login
            const result = await login(email.trim(), password);

            if (result.success) {
                navigate('/admin/dashboard', { replace: true });
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        if (resetLoading) return;

        setResetLoading(true);
        setResetError('');
        setResetSuccess('');

        try {
            // Basic validation
            if (!resetEmail.trim()) {
                throw new Error('Please enter your email address');
            }

            // Basic email validation
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(resetEmail.trim())) {
                throw new Error('Please enter a valid email address');
            }

            // Send reset email
            const result = await resetPassword(resetEmail.trim());

            if (result.success) {
                setResetSuccess(result.message);
                setResetEmail('');
                // Auto close the modal after 3 seconds
                setTimeout(() => {
                    setShowForgotPassword(false);
                    setResetSuccess('');
                }, 3000);
            }
        } catch (err) {
            setResetError(err.message);
        } finally {
            setResetLoading(false);
        }
    };

    const handleCloseForgotPassword = () => {
        setShowForgotPassword(false);
        setResetEmail('');
        setResetError('');
        setResetSuccess('');
    };

    return (
        <div className="admin-login-container">
            <div className="admin-login-card">
                <div className="admin-login-header">
                    <h1>Admin Login</h1>
                    <p>Sign in to access your store dashboard</p>
                </div>

                {error && (
                    <div className="admin-login-error">
                        {error}
                    </div>
                )}

                <form className="admin-login-form" onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">Email Address</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            required
                            placeholder="Enter your email address"
                            autoComplete="email"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-input-container">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                required
                                placeholder="Enter your password"
                                autoComplete="current-password"
                            />
                            {/* <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? '👁️' : '🙈'}
                            </button> */}
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`admin-login-button ${loading ? 'loading' : ''}`}
                    >
                        {loading ? 'Signing In...' : 'Sign In'}
                    </button>

                    <div className="forgot-password-link">
                        <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="link-button"
                            disabled={loading}
                        >
                            Forgot your password?
                        </button>
                    </div>
                </form>


                <div className="admin-login-footer">
                    <p>Don't have an account? <a href="/admin/register">Register Your Store</a></p>

                </div>
            </div>

            {/* Forgot Password Modal */}
            {showForgotPassword && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h3>Reset Password</h3>
                            <button
                                type="button"
                                className="modal-close"
                                onClick={handleCloseForgotPassword}
                                disabled={resetLoading}
                            >
                                ×
                            </button>
                        </div>

                        <div className="modal-body">
                            <p>Enter your email address and we'll send you a link to reset your password.</p>

                            {resetError && (
                                <div className="admin-login-error">
                                    {resetError}
                                </div>
                            )}

                            {resetSuccess && (
                                <div className="admin-login-success">
                                    {resetSuccess}
                                </div>
                            )}

                            <form onSubmit={handleForgotPassword}>
                                <div className="form-group">
                                    <label htmlFor="resetEmail">Email Address</label>
                                    <input
                                        id="resetEmail"
                                        type="email"
                                        value={resetEmail}
                                        onChange={(e) => setResetEmail(e.target.value)}
                                        disabled={resetLoading}
                                        required
                                        placeholder="Enter your email address"
                                        autoComplete="email"
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        onClick={handleCloseForgotPassword}
                                        disabled={resetLoading}
                                        className="admin-login-button secondary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={resetLoading}
                                        className={`admin-login-button ${resetLoading ? 'loading' : ''}`}
                                    >
                                        {resetLoading ? 'Sending...' : 'Send Reset Link'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminLoginPage;